import React, { useCallback, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useApiKey } from '../contexts/ApiKeyContext';
import { validateMedicalFile } from '../services/documentExtraction';
import ApiKeyModal from '../components/ApiKeyModal';
import DisclaimerModal from '../components/DisclaimerModal';
import FileProcessor from '../components/FileProcessor';
import ResultDisplay from '../components/ResultDisplay';
import GlassCard from '../components/GlassCard';
import FuturisticButton from '../components/FuturisticButton';

const {
  FiAlertCircle,
  FiCheck,
  FiCpu,
  FiKey,
  FiSettings,
  FiUpload
} = FiIcons;

const UploadReport = () => {
  const { apiKey, hasApiKey, isValidApiKey } = useApiKey();
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const selectFile = (selectedFile) => {
    try {
      validateMedicalFile(selectedFile);
      setError(null);
      setResult(null);

      if (!disclaimerAccepted) {
        setPendingFile(selectedFile);
        setShowDisclaimerModal(true);
        return;
      }

      if (!hasApiKey || !isValidApiKey) {
        setPendingFile(selectedFile);
        setShowApiKeyModal(true);
        return;
      }

      setFile(selectedFile);
    } catch (selectionError) {
      setError(selectionError.message);
    }
  };

  const handleDisclaimerAccept = () => {
    setDisclaimerAccepted(true);
    setShowDisclaimerModal(false);

    if (!hasApiKey || !isValidApiKey) {
      setShowApiKeyModal(true);
    } else if (pendingFile) {
      setFile(pendingFile);
      setPendingFile(null);
    }
  };

  const handleApiModalClose = () => {
    setShowApiKeyModal(false);

    if (pendingFile && disclaimerAccepted && isValidApiKey) {
      setFile(pendingFile);
      setPendingFile(null);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragOver(false);

    if (event.dataTransfer.files?.[0]) {
      selectFile(event.dataTransfer.files[0]);
    }
  };

  const handleResult = useCallback((analysis) => {
    setResult(analysis);
  }, []);

  const handleError = useCallback((message) => {
    setError(message);
  }, []);

  const handleProcessingChange = useCallback(() => {}, []);

  return (
    <div className="min-h-screen pt-24 sm:pt-28 lg:pt-32 pb-16 relative">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <header className="text-center mb-8">
          <h1 className="text-3xl sm:text-5xl font-bold text-green-300 terminal-text mb-4">
            Medical Report Explanation
          </h1>
          <p className="text-green-100 max-w-2xl mx-auto">
            Extract text from a report and request a plain-language educational
            explanation from OpenAI GPT-4.1.
          </p>
        </header>

        <div className="grid sm:grid-cols-2 gap-3 mb-6">
          <GlassCard className="p-4" hover={false}>
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm text-white flex items-center">
                <SafeIcon
                  icon={isValidApiKey ? FiCheck : FiKey}
                  className={`mr-2 ${isValidApiKey ? 'text-green-400' : 'text-yellow-400'}`}
                />
                {isValidApiKey ? 'OpenAI key available' : 'OpenAI key required'}
              </span>
              <FuturisticButton
                size="sm"
                variant="outline"
                onClick={() => setShowApiKeyModal(true)}
              >
                <SafeIcon icon={hasApiKey ? FiSettings : FiKey} className="h-4 w-4" />
              </FuturisticButton>
            </div>
          </GlassCard>

          <GlassCard className="p-4" hover={false}>
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm text-white flex items-center">
                <SafeIcon
                  icon={disclaimerAccepted ? FiCheck : FiAlertCircle}
                  className={`mr-2 ${disclaimerAccepted ? 'text-green-400' : 'text-yellow-400'}`}
                />
                {disclaimerAccepted ? 'Notice acknowledged' : 'Notice required'}
              </span>
              {!disclaimerAccepted && (
                <FuturisticButton
                  size="sm"
                  variant="outline"
                  onClick={() => setShowDisclaimerModal(true)}
                >
                  Review
                </FuturisticButton>
              )}
            </div>
          </GlassCard>
        </div>

        <GlassCard
          className={`overflow-hidden mb-8 ${dragOver ? 'ring-2 ring-green-400' : ''}`}
          hover={false}
        >
          <div
            onDragOver={(event) => {
              event.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            className="p-6 sm:p-12 text-center"
          >
            <SafeIcon icon={FiUpload} className="h-16 w-16 text-green-400 mx-auto mb-5" />
            <h2 className="text-2xl font-bold text-white terminal-text mb-3">
              Select a Medical Report
            </h2>
            <p className="text-sm text-green-100 max-w-xl mx-auto mb-3">
              PDF, JPEG, PNG, or TXT · Maximum 10 MB
            </p>
            <p className="text-xs text-yellow-200 max-w-xl mx-auto mb-6">
              Remove names, addresses, Medicare numbers, dates of birth, and other
              identifiers whenever possible. Extracted text will be sent to OpenAI.
            </p>
            <div className="max-w-xs mx-auto">
              <FuturisticButton
                icon={FiCpu}
                size="lg"
                onClick={() => fileInputRef.current?.click()}
              >
                Choose Report
              </FuturisticButton>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.txt"
              onChange={(event) => {
                if (event.target.files?.[0]) {
                  selectFile(event.target.files[0]);
                }
                event.target.value = '';
              }}
              className="hidden"
            />
          </div>
        </GlassCard>

        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8"
            >
              <GlassCard className="p-4 ring-1 ring-red-400/50" hover={false}>
                <p className="text-red-300 flex items-start">
                  <SafeIcon icon={FiAlertCircle} className="h-5 w-5 mr-3 mt-0.5" />
                  {error}
                </p>
              </GlassCard>
            </motion.div>
          )}
        </AnimatePresence>

        {file && isValidApiKey && disclaimerAccepted && (
          <FileProcessor
            key={`${file.name}-${file.lastModified}`}
            file={file}
            apiKey={apiKey}
            onResult={handleResult}
            onError={handleError}
            onProcessingChange={handleProcessingChange}
          />
        )}

        {result && <ResultDisplay result={result} />}

        <ApiKeyModal
          isOpen={showApiKeyModal}
          onClose={handleApiModalClose}
        />
        <DisclaimerModal
          isOpen={showDisclaimerModal}
          onClose={() => {
            setShowDisclaimerModal(false);
            setPendingFile(null);
          }}
          onAccept={handleDisclaimerAccept}
        />
      </div>
    </div>
  );
};

export default UploadReport;